import type { SnapshotItem } from '@containd/shared';

/**
 * Freeze thumbnail-source mode codes.
 * Twin of Containd `utils/thumbnailIndexUtils` (`THUMB_MODE_*`) — keep aligned.
 */
export const THUMB_MODE_AUTO = -1;
export const THUMB_MODE_COLOR = -2;
export const THUMB_MODE_TEMPLATE = -3;
export const THUMB_MODE_ELEMENT = -4;
export const THUMB_MODE_CANVAS = -5;

/**
 * Resolve the frozen face media id for a canvas item.
 *
 * Parity with Containd `resolveItemThumbnailUri` (canvas surface):
 * - `>= 0` gallery index → `imageIds[index]` (else `thumbId`)
 * - `-1` / undefined Auto → `thumbId` (host packages image[0] → canvas)
 * - `-2` Color → `null` (no face image)
 * - `-3` Template / `-4` Template element / `-5` Canvas → `thumbId` only.
 *
 * New freezes set `thumbSource`; explicit non-Auto modes never fall back to a
 * gallery photo. Legacy freezes (no `thumbSource`) stored `imageIds[0]` as
 * `thumbId` for explicit modes, so those render color-only.
 */
export function resolveItemFaceMediaId(item: SnapshotItem): string | null {
  const index = item.defaultThumbnailIndex;

  if (item.thumbSource === 'color') return null;
  if (
    item.thumbSource === 'template' ||
    item.thumbSource === 'element' ||
    item.thumbSource === 'canvas'
  ) {
    return item.thumbId ?? null;
  }
  if (item.thumbSource === 'gallery') {
    return galleryMediaId(item);
  }
  if (item.thumbSource === 'auto') {
    return item.thumbId ?? item.imageIds[0] ?? null;
  }

  // Legacy freeze without `thumbSource`.
  if (typeof index === 'number') {
    if (index >= 0) return galleryMediaId(item);
    if (index === THUMB_MODE_AUTO) return item.thumbId ?? item.imageIds[0] ?? null;
    // Explicit -2…-5: old host stored imageIds[0] as thumbId → not trustworthy.
    return null;
  }
  return item.thumbId ?? item.imageIds[0] ?? null;
}

function galleryMediaId(item: SnapshotItem): string | null {
  const index = item.defaultThumbnailIndex;
  if (typeof index === 'number' && index >= 0) {
    return item.imageIds[index] ?? item.thumbId ?? item.imageIds[0] ?? null;
  }
  return item.thumbId ?? item.imageIds[0] ?? null;
}
